import { getNumberOrZero } from '@/common/utils';
import { ErrorAlert, InfoAlert } from '@/components/alerts';
import { useCurrentUser, useScanResult } from '@/hooks';
import { usePrizeEntryCount } from '@/hooks/use-prize-entry-count';
import { PrimaryLayout } from '@/layouts/primary';
import { Scanner } from '@curby/scanner';
import { Text } from '@mantine/core';
import { useSet, useToggle } from '@mantine/hooks';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useMemo } from 'react';
import { useScanAssetFromUserCameraMutation } from './graphql.generated';
import styles from './styles.module.css';

export const ScanCodePage = () => {
	const navigate = useNavigate();
	const { currentUser } = useCurrentUser();
	const { refetch: refetchPrizeEntryCount } = usePrizeEntryCount();
	const goToBagScanned = (entriesGained: number) =>
		navigate({ to: '/bag-scanned', search: { entriesGained } });
	const ignoreCodes = useSet<string>([]);

	const [scanAssetFromUserCamera] = useScanAssetFromUserCameraMutation();

	const infoAlert = useMemo(
		() => (
			<InfoAlert p="xs">
				<Text size="xs" fw={400}>
					Position the QR code in the centre of your camera view to automatically scan. You can also{' '}
					<Link to="/enter-code" className={styles.link}>
						enter a code manually
					</Link>
					.
				</Text>
			</InfoAlert>
		),
		[]
	);

	const [hasCheckedForCamera, setHasCheckedForCamera] = useToggle([false, true]);
	const [hasCamera, setHasCamera] = useToggle([false, true]);

	useEffect(() => {
		navigator.mediaDevices
			.getUserMedia({ video: { facingMode: 'environment' }, audio: false })
			.then((mediaStream) => {
				if (mediaStream) {
					setHasCamera(true);
				}
			})
			.finally(() => {
				setHasCheckedForCamera(true);
			});
	}, [hasCheckedForCamera, setHasCamera, setHasCheckedForCamera]);

	useEffect(() => {
		if (hasCheckedForCamera && !hasCamera) {
			navigate({ to: '/enter-code' });
		}
	}, [hasCamera, hasCheckedForCamera, navigate]);

	const { scanError, setApiErrors } = useScanResult();
	const handleScan = async (code: string) => {
		ignoreCodes.add(code);

		try {
			if (!currentUser?.id) {
				throw new Error('User is not logged in');
			}
			const { errors, data } = await scanAssetFromUserCamera({
				variables: {
					input: {
						scannableCode: code,
						createdByUser: currentUser.id,
					},
				},
				errorPolicy: 'all',
			});

			if (errors === undefined) {
				await refetchPrizeEntryCount();
				goToBagScanned(getNumberOrZero(data?.scanAssetFromUser?.entriesGained));
			}
			setApiErrors(errors);
		} catch (error) {
			ignoreCodes.delete(code);
			console.error(error);
			setApiErrors([error as Error]);
		}
	};

	return (
		<PrimaryLayout
			header={{ backTo: '/home', title: 'Scan QR code' }}
			footer={{ children: infoAlert, p: 'md' }}
		>
			<Scanner onScan={handleScan} ignoreCodes={ignoreCodes} />
			{scanError && (
				<ErrorAlert p="xs">
					<Text size="xs" fw={400}>
						{scanError}. If you think this is an error, please{' '}
						<Link to="/report-issue" className={styles.link}>
							report an issue
						</Link>
						.
					</Text>
				</ErrorAlert>
			)}
		</PrimaryLayout>
	);
};
